<script setup lang="ts">
import { computed, inject, ref, useId, type VNode } from 'vue';
import Dialog, { type DialogPassThroughOptions } from 'primevue/dialog';
import { usePassThrough } from 'primevue/passthrough';
import { overlaySuspendedKey } from '../../overlay-suspended';

import UiDialogCloseButton from './UiDialogCloseButton.vue';

defineOptions({ inheritAttrs: false, name: 'UiDialogShell' });

interface Props {
  closeDisabled?: boolean;
  closeLabel?: string;
  closeOnEscape?: boolean;
  description?: string;
  dismissableMask?: boolean;
  illustration?: string;
  labelledBy?: string;
  maxWidth?: string;
  showPattern?: boolean;
  surface?: 'plain' | 'gradient' | 'gradient-border';
  surfaceClass?: string;
  testId?: string;
  title?: string;
  width?: string;
  pt?: DialogPassThroughOptions;
}

const {
  closeDisabled = false,
  closeLabel = 'Close',
  closeOnEscape = true,
  description,
  dismissableMask = true,
  illustration,
  labelledBy,
  maxWidth = '92vw',
  showPattern = true,
  surface = 'gradient-border',
  surfaceClass,
  testId,
  title,
  width = 'var(--dialog-width-md)',
  pt,
} = defineProps<Props>();

const suspended = inject(overlaySuspendedKey, ref(false));
const dialogPt = computed(() =>
  suspended.value
    ? usePassThrough(
        pt ?? {},
        {
          mask: { style: { display: 'none' }, inert: true },
        },
        { mergeSections: true, mergeProps: true },
      )
    : pt,
);

const visible = defineModel<boolean>('visible', { required: true });
const titleId = useId();

const emit = defineEmits<{
  close: [];
}>();

defineSlots<{
  actions?: () => VNode[];
  body?: () => VNode[];
  illustration?: () => VNode[];
  surface?: () => VNode[];
}>();

const handleVisibilityChange = (nextVisible: boolean) => {
  if ((closeDisabled || suspended.value) && !nextVisible) {
    return;
  }

  const wasVisible = visible.value;

  visible.value = nextVisible;

  if (wasVisible && !nextVisible) {
    emit('close');
  }
};

const handleClose = () => {
  handleVisibilityChange(false);
};
</script>

<template>
  <Dialog
    v-bind="$attrs"
    :visible="visible"
    :pt="dialogPt"
    modal
    :closable="false"
    :draggable="false"
    :showHeader="false"
    :dismissableMask="dismissableMask && !closeDisabled && !suspended"
    :closeOnEscape="closeOnEscape && !closeDisabled && !suspended"
    class="ui-dialog-shell ui-dialog-shell--built-in-container"
    :style="{ width, maxWidth }"
    :aria-labelledby="labelledBy ?? (title ? titleId : undefined)"
    @update:visible="handleVisibilityChange"
  >
    <template #container>
      <div
        class="ui-dialog-shell__surface"
        :class="[
          `ui-dialog-shell__surface--${surface}`,
          { 'ui-dialog-shell__surface--patterned': showPattern },
          { 'ui-dialog-shell__surface--custom-layout': $slots.surface },
          surfaceClass,
        ]"
        :data-testid="testId"
      >
        <slot name="surface">
          <UiDialogCloseButton
            class="ui-dialog-shell__close"
            :disabled="closeDisabled"
            :label="closeLabel"
            @click="handleClose"
          />

          <div class="ui-dialog-shell__content">
            <h2 v-if="title" :id="titleId" class="ui-dialog-shell__title">{{ title }}</h2>

            <slot name="illustration">
              <div v-if="illustration" class="ui-dialog-shell__illustration-frame">
                <img :src="illustration" alt="" class="ui-dialog-shell__illustration" />
              </div>
            </slot>

            <slot name="body">
              <p v-if="description" class="ui-dialog-shell__description">{{ description }}</p>
            </slot>
          </div>

          <div v-if="$slots.actions" class="ui-dialog-shell__actions">
            <slot name="actions" />
          </div>
        </slot>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:global(.p-dialog.ui-dialog-shell--built-in-container) {
  --p-dialog-background: transparent;
  --p-dialog-border-radius: #{$radius-3xl};
  --p-dialog-shadow: none;

  border: 0;
}

.ui-dialog-shell {
  &__surface {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-8;
    padding: ($static-space-10 * 2) $static-space-10 $static-space-10;
    border: 1px solid var(--p-surface-100);
    border-radius: var(--p-dialog-border-radius);
    box-shadow: 0 12px 26.7px 0 rgba(177, 180, 201, 0.26);
    overflow: hidden;

    &--patterned::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      background: url('../../assets/media/dots.png') 0.625rem 0.625rem no-repeat;
      filter: brightness(85.1%);
      opacity: 0.575;
      pointer-events: none;
    }

    &--plain {
      background: var(--p-surface-0);
    }

    &--gradient {
      border-color: color-mix(in srgb, var(--p-surface-300) 75%, transparent);
      background: linear-gradient(180deg, var(--p-surface-0) 0%, #eef3ff 100%);
    }

    &--gradient-border {
      border-color: transparent;
      background:
        linear-gradient(var(--p-surface-0), var(--p-surface-0)) padding-box,
        linear-gradient(180deg, #f2f2f7 0%, #eaedfe 100%) border-box;
    }

    &--custom-layout {
      align-items: stretch;
      gap: 0;
      padding: 0;

      > * {
        position: relative;
        z-index: 1;
      }
    }
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-4;
    width: 100%;
  }

  &__title {
    margin: 0;
    color: var(--p-surface-700);
    font-family: var(--font-family-base);
    font-size: $static-text-xl;
    font-weight: $font-semibold;
    line-height: $leading-none;
    letter-spacing: var(--p-letter-spacing-tight-3);
    text-align: center;
  }

  &__illustration-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 6.25rem;
    padding: $static-space-3;
    border-radius: $radius-2xl;
    background: var(--p-surface-100);
  }

  &__illustration {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__description {
    margin: 0;
    color: var(--p-surface-500);
    font-family: var(--font-family-base);
    font-size: $static-text-sm;
    font-weight: $font-regular;
    line-height: $leading-tight;
    letter-spacing: var(--p-letter-spacing-tight-1);
    text-align: center;
  }

  &__actions {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-3;

    :deep(.p-button) {
      min-height: 2rem;
      padding: 0.4375rem 0.65625rem;
      font-family: var(--font-family-base);
      font-size: $static-text-sm;
      font-weight: $font-medium;
      letter-spacing: var(--p-letter-spacing-tight-1);
    }
  }
}

@media (max-width: 640px) {
  .ui-dialog-shell {
    &__surface {
      &:not(.ui-dialog-shell__surface--custom-layout) {
        padding: $static-space-12 $space-md $space-md;
      }
    }
  }
}
</style>
